import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Bell, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLATFORMS = ["Whatnot", "TikTok", "Both"];

export default function ShowReminders() {
  const [email, setEmail] = useState("");
  const [platform, setPlatform] = useState("Both");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/show-reminders`, { email, platform });
      toast.success(data.message || "You're on the list!");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="show-reminders"
      className="relative bg-[#050505] py-24 md:py-32 overflow-hidden"
    >
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[720px] rounded-full bg-[#3FE1D1]/10 blur-[150px]" />
      <div className="w-full max-w-[1100px] mx-auto px-6 md:px-12 relative">
        <div className="border border-[#262629] bg-[#0f0f10]/70 backdrop-blur-sm p-8 md:p-14">
          <div className="flex flex-col lg:flex-row lg:items-center gap-10">
            <div className="lg:flex-1">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-9 w-9 flex-shrink-0 bg-[#3FE1D1]/15 border border-[#3FE1D1]/40 flex items-center justify-center">
                  <Bell size={16} className="text-[#3FE1D1]" />
                </span>
                <span className="text-xs uppercase tracking-[0.3em] text-[#3FE1D1] font-medium">
                  Show Reminders
                </span>
              </div>
              <h2 className="font-display font-extrabold uppercase tracking-tighter text-white text-3xl md:text-5xl leading-[0.95]">
                Get pinged before <br className="hidden md:block" />
                we go <span className="text-[#3FE1D1]">live.</span>
              </h2>
              <p className="mt-4 text-white/55 text-base md:text-lg max-w-md">
                Drop your email and pick your platform — we'll give you a heads-up
                before the gavel drops.
              </p>
            </div>

            <form onSubmit={submit} className="lg:w-[420px] w-full space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-white/40 mb-2 block">
                  Remind me for
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`reminder-platform-${p}`}
                      onClick={() => setPlatform(p)}
                      className={`py-3 text-xs uppercase tracking-wide border transition-colors ${
                        platform === p
                          ? "bg-[#3FE1D1] border-[#3FE1D1] text-[#050505] font-bold"
                          : "border-[#262629] text-white/70 hover:border-[#3FE1D1]/50"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex">
                <input
                  data-testid="reminder-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="flex-1 bg-[#050505] border border-[#262629] border-r-0 text-white placeholder:text-white/30 px-4 py-3.5 text-sm focus:outline-none focus:border-[#3FE1D1]"
                />
                <button
                  data-testid="reminder-submit"
                  disabled={loading}
                  className="group bg-[#3FE1D1] text-[#050505] font-bold uppercase text-xs tracking-widest px-6 hover:bg-white transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                >
                  Notify Me
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
